// @owners { team: patients-team }
import { ActionSheetContext } from '@alto/design-system';
import React, { useCallback, useContext, useEffect } from 'react';
import { RESTRICTED_PARTNER_ERRORS, getRestrictedPartnerError } from '~shared/selectors/ui/errors/getInsuranceError';
import { useSelectorShared } from '~shared/store';
import { InsuranceWarningActionSheet } from '../components/InsuranceWarningActionSheet';

type Props = {
  handleContinue: () => void;
  handleAddAnotherBenefit?: () => void;
};

export const useInsuranceWarningActionSheet = ({ handleContinue, handleAddAnotherBenefit }: Props) => {
  const { setActiveActionSheet, closeActionSheet } = useContext(ActionSheetContext);
  const error = useSelectorShared(getRestrictedPartnerError);
  const errorCode = error?.details?.code || null;
  const partnerName = error?.details?.internalMessage;

  const continueOnboarding = useCallback(() => {
    handleContinue();
    closeActionSheet();
  }, [closeActionSheet, handleContinue]);

  const addAnotherBenefit = useCallback(() => {
    handleAddAnotherBenefit?.();
    closeActionSheet();
  }, [closeActionSheet, handleAddAnotherBenefit]);

  useEffect(() => {
    if (!errorCode) return;

    setActiveActionSheet(
      <InsuranceWarningActionSheet
        partnerName={partnerName}
        confirmedCoverage={errorCode === RESTRICTED_PARTNER_ERRORS.ALREADY_CONFIRMED}
        handleClose={closeActionSheet}
        handleContinue={continueOnboarding}
        handleAddAnotherBenefit={handleAddAnotherBenefit ? addAnotherBenefit : undefined}
      />,
    );
  }, [
    closeActionSheet,
    setActiveActionSheet,
    addAnotherBenefit,
    handleAddAnotherBenefit,
    continueOnboarding,
    partnerName,
    errorCode,
  ]);

  return { showActionSheet: !!error };
};
